import { createElement, type ComponentType, type ReactElement } from "react";
import type { z } from "zod";

import { MODULES } from "../../app/modules";
import { breakerSelectivityResponseSchema } from "../../services/breakerSelectivity";
import { busbarEvidenceResponseSchema } from "../../services/busbarEvidence";
import { busbarSelectionResponseSchema } from "../../services/busbarSelection";
import { cableSizingResponseSchema } from "../../services/cable";
import { cableTrayResponseSchema } from "../../services/cableTray";
import { earthFaultLoopResponseSchema } from "../../services/earthFaultLoop";
import { shortCircuitStudyResponseSchema } from "../../services/fault";
import { generatorSizingResponseSchema } from "../../services/generatorSizing";
import { harmonicAssessmentResponseSchema } from "../../services/harmonics";
import { loadGroupResponseSchema } from "../../services/loadDemand";
import { pfCorrectionResponseSchema } from "../../services/pfCorrection";
import { pvSizingResponseSchema } from "../../services/pvSizing";
import { starterCoordinationResponseSchema } from "../../services/starterCoordination";
import { earthingResponseSchema } from "../../services/earthing";
import { surgeProtectionResponseSchema } from "../../services/surgeProtection";
import { transformerSizingResponseSchema } from "../../services/transformerSizing";
import { upsSizingResponseSchema } from "../../services/upsSizing";
import type { ValidationLabels } from "../../utils/validationMessages";
import { BreakerSelectivityResultPanel } from "../BreakerSelectivityResultPanel";
import { BreakerSelectivityResultSummary } from "../BreakerSelectivityResultSummary";
import { BreakerSelectivityWarningPanel } from "../BreakerSelectivityWarningPanel";
import { BusbarEvidenceResultPanel } from "../BusbarEvidenceResultPanel";
import { BusbarEvidenceResultSummary } from "../BusbarEvidenceResultSummary";
import { BusbarEvidenceWarningPanel } from "../BusbarEvidenceWarningPanel";
import { BusbarSelectionResultPanel } from "../BusbarSelectionResultPanel";
import { BusbarSelectionResultSummary } from "../BusbarSelectionResultSummary";
import { BusbarSelectionWarningPanel } from "../BusbarSelectionWarningPanel";
import { CableResultSummary } from "../CableResultSummary";
import { CableSizingResultPanel } from "../CableSizingResultPanel";
import { CableTrayResultPanel } from "../CableTrayResultPanel";
import { CableTrayResultSummary } from "../CableTrayResultSummary";
import { CableTrayWarningPanel } from "../CableTrayWarningPanel";
import { CableWarningPanel } from "../CableWarningPanel";
import { EarthFaultLoopResultPanel } from "../EarthFaultLoopResultPanel";
import { EarthFaultLoopResultSummary } from "../EarthFaultLoopResultSummary";
import { EarthFaultLoopWarningPanel } from "../EarthFaultLoopWarningPanel";
import { FaultResultSummary } from "../FaultResultSummary";
import { FaultStudyResultPanel } from "../FaultStudyResultPanel";
import { FaultWarningPanel } from "../FaultWarningPanel";
import { GeneratorResultPanel } from "../GeneratorResultPanel";
import { GeneratorResultSummary } from "../GeneratorResultSummary";
import { GeneratorWarningPanel } from "../GeneratorWarningPanel";
import { HarmonicResultPanel } from "../HarmonicResultPanel";
import { HarmonicResultSummary } from "../HarmonicResultSummary";
import { LoadResultPanel } from "../LoadResultPanel";
import { LoadResultSummary } from "../LoadResultSummary";
import { LoadWarningPanel } from "../LoadWarningPanel";
import { PfCorrectionResultPanel } from "../PfCorrectionResultPanel";
import { PfCorrectionResultSummary } from "../PfCorrectionResultSummary";
import { PowerQualityWarningPanel } from "../PowerQualityWarningPanel";
import { PVResultPanel } from "../PVResultPanel";
import { PVResultSummary } from "../PVResultSummary";
import { PVWarningPanel } from "../PVWarningPanel";
import { StarterCoordinationResultPanel } from "../StarterCoordinationResultPanel";
import { StarterCoordinationResultSummary } from "../StarterCoordinationResultSummary";
import { StarterCoordinationWarningPanel } from "../StarterCoordinationWarningPanel";
import { EarthingResultPanel } from "../EarthingResultPanel";
import { EarthingResultSummary } from "../EarthingResultSummary";
import { EarthingWarningPanel } from "../EarthingWarningPanel";
import { SurgeProtectionResultPanel } from "../SurgeProtectionResultPanel";
import { SurgeProtectionResultSummary } from "../SurgeProtectionResultSummary";
import { SurgeProtectionWarningPanel } from "../SurgeProtectionWarningPanel";
import { TransformerResultPanel } from "../TransformerResultPanel";
import { TransformerResultSummary } from "../TransformerResultSummary";
import { TransformerWarningPanel } from "../TransformerWarningPanel";
import { UPSResultPanel } from "../UPSResultPanel";
import { UPSResultSummary } from "../UPSResultSummary";
import { UPSWarningPanel } from "../UPSWarningPanel";
import { BREAKER_SELECTIVITY_LABELS } from "../breakerSelectivityDraft";
import { BUSBAR_EVIDENCE_LABELS } from "../busbarEvidenceDraft";
import { BUSBAR_SELECTION_LABELS } from "../busbarSelectionDraft";
import { CABLE_SIZING_LABELS } from "../cableSizingLabels";
import { CABLE_TRAY_LABELS } from "../cableTrayLabels";
import { EARTH_FAULT_LOOP_LABELS } from "../earthFaultLoopDraft";
import { EARTHING_LABELS } from "../earthingLabels";
import { FAULT_STUDY_LABELS } from "../faultStudyDraft";
import { GENERATOR_STUDY_LABELS } from "../generatorStudyDraft";
import { HARMONICS_LABELS } from "../harmonicsLabels";
import { LOAD_STUDY_LABELS } from "../loadStudyDraft";
import { PF_CORRECTION_LABELS } from "../pfCorrectionLabels";
import { PV_STUDY_LABELS } from "../pvStudyDraft";
import { STARTER_COORDINATION_LABELS } from "../starterCoordinationDraft";
import { SURGE_PROTECTION_LABELS } from "../surgeProtectionLabels";
import { TRANSFORMER_STUDY_LABELS } from "../transformerStudyDraft";
import { UPS_STUDY_LABELS } from "../upsStudyDraft";

// The section registry of the printable report (EOS-13 design note section 3, Master Prompt
// A31): one entry per calculation type the backend stores (EngineeringCalculationType in
// backend/app/models/calculation_run.py), giving the module's result contract schema, its own
// result summary, detail panel and warning panel, its form label map and its module name.
// reportSections.test.ts walks the backend's enum and fails when a type has no entry, so a
// report can never silently print a module without its sections. The blocks render the
// stored result snapshot exactly as the study page renders a fresh result: nothing is
// recomputed. A snapshot the module's current contract cannot parse (an older engine version)
// gives null blocks and the page prints the raw snapshot instead - never a crash.

/** The three result blocks of a module, built from its parsed result snapshot. */
export type ReportResultBlocks = {
  summary: ReactElement;
  checks: ReactElement;
  warnings: ReactElement;
};

export type ReportSection = {
  calculationType: string;
  moduleCode: string;
  /** The module's name in app/modules.ts (the backend prints the same name). */
  moduleName: string;
  /** The form's label map, so the inputs section names a field the way the form does. */
  inputLabels: ValidationLabels;
  /**
   * The module's blocks for a stored result snapshot, or null when the contract cannot read it.
   * The engine version that produced the run names the engine behind a field it did not
   * record (F1: "Not recorded (engine ... predates this field)").
   */
  blocks(resultSnapshot: unknown, context?: ReportBlockContext): ReportResultBlocks | null;
};

export type ReportBlockContext = {
  engineVersion?: string;
};

type SectionDefinition<T> = {
  calculationType: string;
  moduleCode: string;
  resultSchema: z.ZodType<T>;
  ResultSummary: ComponentType<{ result: T; engineVersion?: string }>;
  ResultPanel: ComponentType<{ result: T; engineVersion?: string }>;
  WarningPanel: ComponentType<{ result: T; engineVersion?: string }>;
  inputLabels: ValidationLabels;
};

function moduleNameOf(code: string): string {
  const module = MODULES.find((entry) => entry.code === code);
  if (module === undefined) {
    throw new Error(`Report section registry: module ${code} is not in app/modules.ts`);
  }
  return module.name;
}

function section<T>(definition: SectionDefinition<T>): ReportSection {
  const { resultSchema, ResultSummary, ResultPanel, WarningPanel } = definition;
  return {
    calculationType: definition.calculationType,
    moduleCode: definition.moduleCode,
    moduleName: moduleNameOf(definition.moduleCode),
    inputLabels: definition.inputLabels,
    blocks(resultSnapshot: unknown, context?: ReportBlockContext): ReportResultBlocks | null {
      const parsed = resultSchema.safeParse(resultSnapshot);
      if (!parsed.success) {
        return null;
      }
      const result = parsed.data;
      const engineVersion = context?.engineVersion;
      return {
        summary: createElement(ResultSummary, { result, engineVersion }),
        checks: createElement(ResultPanel, { result, engineVersion }),
        warnings: createElement(WarningPanel, { result, engineVersion }),
      };
    },
  };
}

// The warning panels that take the result's warning list rather than the whole result.
function warningsOf<W>(Panel: ComponentType<{ warnings: W[] }>) {
  return function ResultWarnings<T extends { warnings: W[] }>({ result }: { result: T }) {
    return createElement(Panel, { warnings: result.warnings });
  };
}

const SECTIONS: readonly ReportSection[] = [
  section({
    calculationType: "CABLE_SIZING",
    moduleCode: "EOS-06",
    resultSchema: cableSizingResponseSchema,
    ResultSummary: CableResultSummary,
    ResultPanel: CableSizingResultPanel,
    WarningPanel: warningsOf(CableWarningPanel),
    inputLabels: CABLE_SIZING_LABELS,
  }),
  section({
    calculationType: "SHORT_CIRCUIT",
    moduleCode: "EOS-04",
    resultSchema: shortCircuitStudyResponseSchema,
    ResultSummary: FaultResultSummary,
    ResultPanel: FaultStudyResultPanel,
    WarningPanel: warningsOf(FaultWarningPanel),
    inputLabels: FAULT_STUDY_LABELS,
  }),
  section({
    calculationType: "LOAD_DEMAND",
    moduleCode: "EOS-02",
    resultSchema: loadGroupResponseSchema,
    ResultSummary: LoadResultSummary,
    ResultPanel: LoadResultPanel,
    WarningPanel: warningsOf(LoadWarningPanel),
    inputLabels: LOAD_STUDY_LABELS,
  }),
  section({
    calculationType: "TRANSFORMER_SIZING",
    moduleCode: "EOS-03",
    resultSchema: transformerSizingResponseSchema,
    ResultSummary: TransformerResultSummary,
    ResultPanel: TransformerResultPanel,
    WarningPanel: warningsOf(TransformerWarningPanel),
    inputLabels: TRANSFORMER_STUDY_LABELS,
  }),
  section({
    calculationType: "GENERATOR_SIZING",
    moduleCode: "EOS-03",
    resultSchema: generatorSizingResponseSchema,
    ResultSummary: GeneratorResultSummary,
    ResultPanel: GeneratorResultPanel,
    WarningPanel: warningsOf(GeneratorWarningPanel),
    inputLabels: GENERATOR_STUDY_LABELS,
  }),
  section({
    calculationType: "UPS_SIZING",
    moduleCode: "EOS-03",
    resultSchema: upsSizingResponseSchema,
    ResultSummary: UPSResultSummary,
    ResultPanel: UPSResultPanel,
    WarningPanel: warningsOf(UPSWarningPanel),
    inputLabels: UPS_STUDY_LABELS,
  }),
  section({
    calculationType: "PV_SIZING",
    moduleCode: "EOS-03",
    resultSchema: pvSizingResponseSchema,
    ResultSummary: PVResultSummary,
    ResultPanel: PVResultPanel,
    WarningPanel: warningsOf(PVWarningPanel),
    inputLabels: PV_STUDY_LABELS,
  }),
  section({
    calculationType: "STARTER_COORDINATION",
    moduleCode: "EOS-05",
    resultSchema: starterCoordinationResponseSchema,
    ResultSummary: StarterCoordinationResultSummary,
    ResultPanel: StarterCoordinationResultPanel,
    WarningPanel: StarterCoordinationWarningPanel,
    inputLabels: STARTER_COORDINATION_LABELS,
  }),
  section({
    calculationType: "BREAKER_SELECTIVITY",
    moduleCode: "EOS-05",
    resultSchema: breakerSelectivityResponseSchema,
    ResultSummary: BreakerSelectivityResultSummary,
    ResultPanel: BreakerSelectivityResultPanel,
    WarningPanel: BreakerSelectivityWarningPanel,
    inputLabels: BREAKER_SELECTIVITY_LABELS,
  }),
  section({
    calculationType: "BUSBAR_EVIDENCE",
    moduleCode: "EOS-07",
    resultSchema: busbarEvidenceResponseSchema,
    ResultSummary: BusbarEvidenceResultSummary,
    ResultPanel: BusbarEvidenceResultPanel,
    WarningPanel: BusbarEvidenceWarningPanel,
    inputLabels: BUSBAR_EVIDENCE_LABELS,
  }),
  section({
    calculationType: "BUSBAR_SELECTION",
    moduleCode: "EOS-07",
    resultSchema: busbarSelectionResponseSchema,
    ResultSummary: BusbarSelectionResultSummary,
    ResultPanel: BusbarSelectionResultPanel,
    WarningPanel: BusbarSelectionWarningPanel,
    inputLabels: BUSBAR_SELECTION_LABELS,
  }),
  section({
    calculationType: "POWER_FACTOR_CORRECTION",
    moduleCode: "EOS-11",
    resultSchema: pfCorrectionResponseSchema,
    ResultSummary: PfCorrectionResultSummary,
    ResultPanel: PfCorrectionResultPanel,
    WarningPanel: warningsOf(PowerQualityWarningPanel),
    inputLabels: PF_CORRECTION_LABELS,
  }),
  section({
    calculationType: "HARMONIC_ASSESSMENT",
    moduleCode: "EOS-11",
    resultSchema: harmonicAssessmentResponseSchema,
    ResultSummary: HarmonicResultSummary,
    ResultPanel: HarmonicResultPanel,
    WarningPanel: warningsOf(PowerQualityWarningPanel),
    inputLabels: HARMONICS_LABELS,
  }),
  section({
    calculationType: "EARTH_FAULT_LOOP",
    moduleCode: "EOS-05",
    resultSchema: earthFaultLoopResponseSchema,
    ResultSummary: EarthFaultLoopResultSummary,
    ResultPanel: EarthFaultLoopResultPanel,
    WarningPanel: EarthFaultLoopWarningPanel,
    inputLabels: EARTH_FAULT_LOOP_LABELS,
  }),
  section({
    calculationType: "CABLE_TRAY",
    moduleCode: "EOS-12",
    resultSchema: cableTrayResponseSchema,
    ResultSummary: CableTrayResultSummary,
    ResultPanel: CableTrayResultPanel,
    WarningPanel: CableTrayWarningPanel,
    inputLabels: CABLE_TRAY_LABELS,
  }),
  section({
    calculationType: "SURGE_PROTECTION",
    moduleCode: "EOS-10",
    resultSchema: surgeProtectionResponseSchema,
    ResultSummary: SurgeProtectionResultSummary,
    ResultPanel: SurgeProtectionResultPanel,
    WarningPanel: SurgeProtectionWarningPanel,
    inputLabels: SURGE_PROTECTION_LABELS,
  }),
  section({
    calculationType: "EARTHING",
    moduleCode: "EOS-08",
    resultSchema: earthingResponseSchema,
    ResultSummary: EarthingResultSummary,
    ResultPanel: EarthingResultPanel,
    WarningPanel: EarthingWarningPanel,
    inputLabels: EARTHING_LABELS,
  }),
];

/** The registry, keyed by the stored calculation_type. */
export const REPORT_SECTIONS: Readonly<Record<string, ReportSection>> = Object.fromEntries(
  SECTIONS.map((entry) => [entry.calculationType, entry]),
);

/** The section of a stored run's calculation type, or undefined when no module lays it out. */
export function reportSectionOf(calculationType: string): ReportSection | undefined {
  return Object.hasOwn(REPORT_SECTIONS, calculationType)
    ? REPORT_SECTIONS[calculationType]
    : undefined;
}
